// src/pages/Campaigns/SurveyGenie/SurveyDashboard.js
import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Button, Paper, Grid, Card, CardContent, CardActions,
  Chip, IconButton, Menu, MenuItem, TextField, InputAdornment,
  Dialog, DialogTitle, DialogContent, DialogActions, FormControl,
  InputLabel, Select, Alert, Pagination, CircularProgress, Stack,
  Tooltip, Divider, Tab, Tabs, LinearProgress, ListItemIcon, ListItemText,
  Checkbox, Badge, Snackbar  // NEW: Added Checkbox, Badge, Snackbar
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ContentCopy as DuplicateIcon,
  Visibility as ViewIcon,
  Share as ShareIcon,
  Assessment as AnalyticsIcon,
  PlayArrow as PublishIcon,
  Pause as PauseIcon,
  Poll as PollIcon,
  QuestionAnswer as ResponseIcon,
  CleaningServices,
  Rule,
  CheckCircle as CheckCircleIcon,
  RemoveCircleOutline as UntrainIcon,
  Description,
} from '@mui/icons-material';
import ModelTrainingIcon from '@mui/icons-material/ModelTraining';
import { useParams, useNavigate } from 'react-router-dom';
import axiosInstance from '../../../utils/axiosInstance';
import { hasPermission } from '../../../utils/permissionUtils';
import {
  SurveyStatus,
  SurveyStatusLabels,
  SurveyStatusColors,
  SurveyTemplateCategory,
  SurveyTemplateCategoryLabels
} from './surveyEnums';

const SurveyDashboard = () => {
  const { chatbotId } = useParams();
  const navigate = useNavigate();

  // State
  const [surveys, setSurveys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [tabValue, setTabValue] = useState(0);

  // Permissions
  const [canView, setCanView] = useState(false);
  const [canModify, setCanModify] = useState(false);
  const [canDelete, setCanDelete] = useState(false);
  const [checkingPermissions, setCheckingPermissions] = useState(true);

  // Dialog states
  const [createDialog, setCreateDialog] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, survey: null });
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [selectedSurvey, setSelectedSurvey] = useState(null);

  // Create form
  const [newSurvey, setNewSurvey] = useState({
    title: '',
    description: '',
    template_category: ''
  });

  // Snackbar
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

  // ============== NEW: Training States ==============
  const [selectedSurveys, setSelectedSurveys] = useState([]);
  const [isTraining, setIsTraining] = useState(false);
  const [isUntraining, setIsUntraining] = useState(false);
  // ==================================================

  // Check permissions
  useEffect(() => {
    const checkPermissions = async () => {
      try {
        const viewPerm = await hasPermission('survey_genie', 'view');
        const modifyPerm = await hasPermission('survey_genie', 'add|edit');
        const deletePerm = await hasPermission('survey_genie', 'delete');
        setCanView(viewPerm);
        setCanModify(modifyPerm);
        setCanDelete(deletePerm);
      } catch (error) {
        console.error('Error checking permissions:', error);
      } finally {
        setCheckingPermissions(false);
      }
    };
    checkPermissions();
  }, []);

  // Load surveys
  useEffect(() => {
    if (canView && chatbotId) {
      const timeoutId = setTimeout(() => {
        loadSurveys();
      }, 500);
      return () => clearTimeout(timeoutId);
    }
  }, [canView, chatbotId, page, statusFilter, searchTerm, tabValue]);

  const loadSurveys = async () => {
    setLoading(true);
    try {
      const params = {
        chatbot_id: chatbotId,
        page,
        per_page: 12
      };

      if (statusFilter) params.status = statusFilter;
      if (searchTerm) params.search = searchTerm;

      const response = await axiosInstance.get('/api/surveys', { params });
      setSurveys(response.data.data);
      setTotalPages(response.data.pagination.pages);
    } catch (error) {
      showSnackbar('Error loading surveys', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSurvey = async () => {
    if (!newSurvey.title.trim()) {
      showSnackbar('Survey title is required', 'error');
      return;
    }

    try {
      const response = await axiosInstance.post('/api/surveys', newSurvey, {
        params: { chatbot_id: chatbotId }
      });

      showSnackbar('Survey created successfully', 'success');
      setCreateDialog(false);
      setNewSurvey({ title: '', description: '', template_category: '' });

      // Navigate to builder
      navigate(`/chatbot/${chatbotId}/surveys/builder/${response.data.data.id}`);
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Error creating survey', 'error');
    }
  };

  const handleUpdateStatus = async (surveyId, newStatus) => {
    try {
      await axiosInstance.put('/api/surveys', { status: newStatus }, {
        params: { survey_id: surveyId }
      });
      showSnackbar('Survey status updated', 'success');
      loadSurveys();
    } catch (error) {
      showSnackbar('Error updating status', 'error');
    }
    handleMenuClose();
  };

  const handleDuplicateSurvey = async (surveyId) => {
    try {
      const response = await axiosInstance.post('/api/surveys/duplicate', {}, {
        params: { survey_id: surveyId }
      });
      showSnackbar('Survey duplicated successfully', 'success');
      loadSurveys();
    } catch (error) {
      showSnackbar('Error duplicating survey', 'error');
    }
    handleMenuClose();
  };

  const handleDeleteSurvey = async () => {
    if (!deleteDialog.survey) return;

    try {
      await axiosInstance.delete('/api/surveys', {
        params: { survey_id: deleteDialog.survey.id }
      });
      showSnackbar('Survey deleted successfully', 'success');
      setDeleteDialog({ open: false, survey: null });
      loadSurveys();
    } catch (error) {
      showSnackbar('Error deleting survey', 'error');
    }
  };

  const handleMenuOpen = (event, survey) => {
    setMenuAnchor(event.currentTarget);
    setSelectedSurvey(survey);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setSelectedSurvey(null);
  };

  const showSnackbar = (message, severity = 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const getStatusChip = (status) => {
    return (
      <Chip
        label={SurveyStatusLabels[status]}
        color={SurveyStatusColors[status]}
        size="small"
      />
    );
  };

  // ============== NEW: Training Selection Handlers ==============
  const handleSelectSurvey = (surveyId) => {
    setSelectedSurveys(prev => {
      if (prev.includes(surveyId)) {
        return prev.filter(id => id !== surveyId);
      }
      return [...prev, surveyId];
    });
  };

  const handleSelectAll = () => {
    if (selectedSurveys.length === surveys.length) {
      setSelectedSurveys([]);
    } else {
      setSelectedSurveys(surveys.map(s => s.id));
    }
  };

  // Check if any selected survey is trained/untrained
  const hasSelectedTrainedSurveys = () => {
    return selectedSurveys.some(surveyId => {
      const survey = surveys.find(s => s.id === surveyId);
      return survey && survey.is_trained;
    });
  };

  const hasSelectedUntrainedSurveys = () => {
    return selectedSurveys.some(surveyId => {
      const survey = surveys.find(s => s.id === surveyId);
      return survey && !survey.is_trained;
    });
  };

  // Train selected surveys
  const handleTrainSelectedSurveys = async () => {
    const untrainedSurveyIds = selectedSurveys.filter(surveyId => {
      const survey = surveys.find(s => s.id === surveyId);
      return survey && !survey.is_trained;
    });

    if (untrainedSurveyIds.length === 0) {
      showSnackbar('No untrained surveys selected', 'warning');
      return;
    }

    setIsTraining(true);
    try {
      const response = await axiosInstance.post('/api/surveys/training/train',
        { survey_ids: untrainedSurveyIds },
        { params: { chatbot_id: chatbotId } }
      );

      if (response.data.success) {
        showSnackbar(response.data.message || 'Surveys trained to Knowledge base successfully', 'success');
        setSelectedSurveys([]);
        loadSurveys();
      }
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Error training surveys', 'error');
    } finally {
      setIsTraining(false);
    }
  };

  // Untrain selected surveys
  const handleUntrainSelectedSurveys = async () => {
    const trainedSurveyIds = selectedSurveys.filter(surveyId => {
      const survey = surveys.find(s => s.id === surveyId);
      return survey && survey.is_trained;
    });

    if (trainedSurveyIds.length === 0) {
      showSnackbar('No trained surveys selected', 'warning');
      return;
    }

    if (!window.confirm(`Are you sure you want to remove ${trainedSurveyIds.length} survey(s) from training? This will delete them from the knowledge base.`)) {
      return;
    }

    setIsUntraining(true);
    try {
      const response = await axiosInstance.post('/api/surveys/training/untrain',
        { survey_ids: trainedSurveyIds },
        { params: { chatbot_id: chatbotId } }
      );

      if (response.data.success) {
        showSnackbar(response.data.message || 'Surveys removed from Knowledge base successfully', 'success');
        setSelectedSurveys([]);
        loadSurveys();
      }
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Error removing surveys from training', 'error');
    } finally {
      setIsUntraining(false);
    }
  };
  // ==============================================================

  if (checkingPermissions) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!canView) {
    return (
      <Alert severity="error" sx={{ mt: 6 }}>
        <strong>Access Denied</strong><br />
        You don't have permission to view Survey Management.
      </Alert>
    );
  }

  return (
    <Box sx={{ p: 3, mt: 10 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight={700}>
            <PollIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            SurveyGenie
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Create, distribute, and analyze surveys
          </Typography>
        </Box>
        {canModify && (
          <Stack direction="row" spacing={2}>
            <Button
              variant="outlined"
              onClick={() => navigate(`/chatbot/${chatbotId}/surveys/question-bank`)}
            >
              Question Bank
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setCreateDialog(true)}
            >
              Create Survey
            </Button>
          </Stack>
        )}
      </Box>

      {/* ============== NEW: Training Action Bar ============== */}
      {selectedSurveys.length > 0 && canModify && (
        <Paper sx={{ p: 2, mb: 2, display: 'flex', alignItems: 'center', gap: 2, backgroundColor: 'primary.50' }}>
          <Chip
            label={`${selectedSurveys.length} survey(s) selected`}
            color="primary"
            variant="outlined"
          />
          {hasSelectedUntrainedSurveys() && (
            <Button
              variant="contained"
              color="primary"
              startIcon={isTraining ? <CircularProgress size={20} color="inherit" /> : <ModelTrainingIcon />}
              onClick={handleTrainSelectedSurveys}
              disabled={isTraining || isUntraining}
            >
              {isTraining ? 'Training...' : 'Train Selected'}
            </Button>
          )}
          {hasSelectedTrainedSurveys() && (
            <Button
              variant="outlined"
              color="warning"
              startIcon={isUntraining ? <CircularProgress size={20} color="inherit" /> : <UntrainIcon />}
              onClick={handleUntrainSelectedSurveys}
              disabled={isTraining || isUntraining}
            >
              {isUntraining ? 'Untraining...' : 'Untrain Selected'}
            </Button>
          )}
          <Button
            variant="text"
            size="small"
            onClick={() => setSelectedSurveys([])}
          >
            Clear Selection
          </Button>
        </Paper>
      )}
      {/* ======================================================= */}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search surveys..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setPage(1);
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                )
              }}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setPage(1);
                }}
                label="Status"
              >
                <MenuItem value="">All</MenuItem>
                {Object.entries(SurveyStatusLabels).map(([key, label]) => (
                  <MenuItem key={key} value={key}>{label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          {/* NEW: Select All Button */}
          <Grid item xs={12} md={2}>
            <Button
              variant="outlined"
              size="small"
              onClick={handleSelectAll}
              fullWidth
            >
              {selectedSurveys.length === surveys.length && surveys.length > 0 ? 'Deselect All' : 'Select All'}
            </Button>
          </Grid>
          <Grid item xs={12} md={4}>
            <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)}>
              <Tab label="All Surveys" />
              <Tab label="Active" />
              <Tab label="Drafts" />
            </Tabs>
          </Grid>
        </Grid>
      </Paper>

      {/* Survey Grid */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
          <CircularProgress />
        </Box>
      ) : surveys.length === 0 ? (
        <Paper sx={{ p: 5, textAlign: 'center' }}>
          <PollIcon sx={{ fontSize: 64, color: 'grey.400', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No surveys found
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {searchTerm || statusFilter
              ? 'Try adjusting your filters'
              : 'Get started by creating your first survey'}
          </Typography>
          {canModify && !searchTerm && !statusFilter && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setCreateDialog(true)}
            >
              Create Survey
            </Button>
          )}
        </Paper>
      ) : (
        <>
          <Grid container spacing={3}>
            {surveys.map((survey) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={survey.id}>
                <Card
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    transition: '0.3s',
                    position: 'relative',  // NEW: For absolute positioning
                    // NEW: Border when selected
                    border: selectedSurveys.includes(survey.id) ? '2px solid' : '1px solid',
                    borderColor: selectedSurveys.includes(survey.id) ? 'primary.main' : 'divider',
                    '&:hover': {
                      boxShadow: 6,
                      transform: 'translateY(-4px)'
                    }
                  }}
                >
                  {/* NEW: Selection Checkbox */}
                  <Checkbox
                    checked={selectedSurveys.includes(survey.id)}
                    onChange={() => handleSelectSurvey(survey.id)}
                    sx={{ position: 'absolute', top: 4, left: 4, zIndex: 1 }}
                    size="small"
                  />

                  {/* NEW: Training Status Badge */}
                  {survey.is_trained && (
                    <Tooltip title={`Trained${survey.trained_at ? ` on ${new Date(survey.trained_at).toLocaleDateString()}` : ''}`}>
                      <Box sx={{ position: 'absolute', top: 8, right: 40, zIndex: 1 }}>
                        <Badge
                          badgeContent={<CheckCircleIcon sx={{ fontSize: 12, color: 'success.main' }} />}
                          overlap="circular"
                        >
                          <ModelTrainingIcon color="success" fontSize="small" />
                        </Badge>
                      </Box>
                    </Tooltip>
                  )}

                  <CardContent sx={{ flexGrow: 1, pt: 5 }}> {/* NEW: Added pt: 5 for checkbox space */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                      {getStatusChip(survey.status)}
                      <IconButton size="small" onClick={(e) => handleMenuOpen(e, survey)}>
                        <MoreVertIcon />
                      </IconButton>
                    </Box>

                    <Typography variant="h6" gutterBottom noWrap>
                      {survey.title}
                    </Typography>

                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2, height: 40, overflow: 'hidden' }}>
                      {survey.description || 'No description'}
                    </Typography>

                    <Divider sx={{ my: 1 }} />

                    <Grid container spacing={1}>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">
                          Questions
                        </Typography>
                        <Typography variant="body2" fontWeight={600}>
                          {survey.question_count || 0}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">
                          Responses
                        </Typography>
                        <Typography variant="body2" fontWeight={600}>
                          {survey.completed_responses || 0}
                        </Typography>
                      </Grid>
                    </Grid>

                    {/* NEW: Training Status Chip */}
                    {survey.is_trained && (
                      <Chip
                        icon={<ModelTrainingIcon />}
                        label="Trained"
                        size="small"
                        color="success"
                        variant="outlined"
                        sx={{ mt: 1 }}
                      />
                    )}

                    {survey.completion_rate !== null && (
                      <Box sx={{ mt: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                          <Typography variant="caption">Completion Rate</Typography>
                          <Typography variant="caption">{survey.completion_rate?.toFixed(1)}%</Typography>
                        </Box>
                        <LinearProgress
                          variant="determinate"
                          value={survey.completion_rate || 0}
                          sx={{ height: 6, borderRadius: 3 }}
                        />
                      </Box>
                    )}
                  </CardContent>

                  <CardActions sx={{ p: 2, pt: 0, flexWrap: 'wrap', gap: 1 }}> 
                    <Button
                      size="small"
                      startIcon={<EditIcon />}
                      onClick={() => navigate(`/chatbot/${chatbotId}/surveys/builder/${survey.id}`)}
                      sx={{ minWidth: 'auto' }}
                    >
                      Edit
                    </Button>
                    <Button
                      size="small"
                      startIcon={<ResponseIcon />}
                      onClick={() => navigate(`/chatbot/${chatbotId}/surveys/responses/${survey.id}`)}
                      sx={{ minWidth: 'auto' }}
                    >
                      Responses
                    </Button>
                    <Button
                      size="small"
                      startIcon={<AnalyticsIcon />}
                      onClick={() => navigate(`/chatbot/${chatbotId}/surveys/analytics/${survey.id}`)}
                      sx={{ minWidth: 'auto' }}
                    >
                      Analytics
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* Pagination */}
          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={(e, newPage) => setPage(newPage)}
                color="primary"
              />
            </Box>
          )}
        </>
      )}

      {/* Action Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => {
          handleMenuClose();
          navigate(`/chatbot/${chatbotId}/surveys/builder/${selectedSurvey?.id}`);
        }}>
          <EditIcon sx={{ mr: 1 }} fontSize="small" />
          Edit Survey
        </MenuItem>
        <MenuItem onClick={() => {
          handleMenuClose();
          navigate(`/chatbot/${chatbotId}/surveys/distribution/${selectedSurvey?.id}`);
        }}>
          <ShareIcon sx={{ mr: 1 }} fontSize="small" />
          Distribute
        </MenuItem>
        <MenuItem onClick={() => {
          handleMenuClose();
          navigate(`/chatbot/${chatbotId}/surveys/responses/${selectedSurvey?.id}`);
        }}>
          <ResponseIcon sx={{ mr: 1 }} fontSize="small" />
          View Responses
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => {
            handleMenuClose();
            navigate(`/chatbot/${chatbotId}/surveys/documents/${selectedSurvey?.id}`);
        }}>
            <ListItemIcon><Description fontSize="small" /></ListItemIcon>
            <ListItemText>Manage Documents</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => {
            handleMenuClose();
            navigate(`/chatbot/${chatbotId}/surveys/faqs/${selectedSurvey?.id}`);
        }}>
            <ListItemIcon><ResponseIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Manage FAQs</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => {
            handleMenuClose();
            navigate(`/chatbot/${chatbotId}/surveys/cleaning/${selectedSurvey?.id}`);
        }}>
            <ListItemIcon><CleaningServices fontSize="small" /></ListItemIcon>
            <ListItemText>Data Cleaning Tools</ListItemText>
        </MenuItem>
        
        <MenuItem onClick={() => {
            handleMenuClose();
            navigate(`/chatbot/${chatbotId}/surveys/cleaning/rules/${selectedSurvey?.id}`);
        }}>
            <ListItemIcon><Rule fontSize="small" /></ListItemIcon>
            <ListItemText>Cleaning Rules</ListItemText>
        </MenuItem>
        <Divider />
        {selectedSurvey?.status === SurveyStatus.DRAFT && (
          <MenuItem onClick={() => handleUpdateStatus(selectedSurvey.id, SurveyStatus.ACTIVE)}>
            <PublishIcon sx={{ mr: 1 }} fontSize="small" />
            Publish
          </MenuItem>
        )}
        {selectedSurvey?.status === SurveyStatus.ACTIVE && (
          <MenuItem onClick={() => handleUpdateStatus(selectedSurvey.id, SurveyStatus.PAUSED)}>
            <PauseIcon sx={{ mr: 1 }} fontSize="small" />
            Pause
          </MenuItem>
        )}
        <MenuItem onClick={() => handleDuplicateSurvey(selectedSurvey?.id)}>
          <DuplicateIcon sx={{ mr: 1 }} fontSize="small" />
          Duplicate
        </MenuItem>
        {canDelete && (
          <>
            <Divider />
            <MenuItem
              onClick={() => {
                handleMenuClose();
                setDeleteDialog({ open: true, survey: selectedSurvey });
              }}
              sx={{ color: 'error.main' }}
            >
              <DeleteIcon sx={{ mr: 1 }} fontSize="small" />
              Delete
            </MenuItem>
          </>
        )}
      </Menu>

      {/* Create Survey Dialog */}
      <Dialog open={createDialog} onClose={() => setCreateDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create New Survey</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Survey Title"
            value={newSurvey.title}
            onChange={(e) => setNewSurvey({ ...newSurvey, title: e.target.value })}
            sx={{ mt: 2, mb: 2 }}
            required
          />
          <TextField
            fullWidth
            label="Description"
            value={newSurvey.description}
            onChange={(e) => setNewSurvey({ ...newSurvey, description: e.target.value })}
            multiline
            rows={3}
            sx={{ mb: 2 }}
          />
          <FormControl fullWidth>
            <InputLabel>Category (Optional)</InputLabel>
            <Select
              value={newSurvey.template_category}
              onChange={(e) => setNewSurvey({ ...newSurvey, template_category: e.target.value })}
              label="Category (Optional)"
            >
              <MenuItem value="">None</MenuItem>
              {Object.entries(SurveyTemplateCategoryLabels).map(([key, label]) => (
                <MenuItem key={key} value={key}>{label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreateSurvey}>
            Create & Build
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, survey: null })}>
        <DialogTitle>Delete Survey</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{deleteDialog.survey?.title}"?
            This action will archive the survey and all its responses.
          </Typography>
          {/* NEW: Warning if survey is trained */}
          {deleteDialog.survey?.is_trained && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              This survey is trained in the knowledge base. Deleting it will also remove it from training.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, survey: null })}>
            Cancel
          </Button>
          <Button variant="contained" color="error" onClick={handleDeleteSurvey}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* NEW: Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SurveyDashboard;